import React from 'react';
import Icon from '../../../components/AppIcon';


const ModeSelector = ({ 
  currentMode = 'fill-blank', 
  onModeChange, 
  className = '' 
}) => {
  const modes = [
    {
      id: 'chunking',
      label: 'Progressive Chunking',
      description: 'Build verse progressively with repetition',
      icon: 'Layers',
      color: 'var(--color-primary)',
      activeBg: 'bg-primary/15',
      activeBorder: 'border-primary/60',
      activeGradient: 'from-primary/10 via-primary/5 to-transparent',
      recommended: true
    },
    {
      id: 'word-order',
      label: 'Word Order',
      description: 'Arrange scrambled words correctly',
      icon: 'Shuffle',
      color: 'var(--color-accent)',
      activeBg: 'bg-accent/15',
      activeBorder: 'border-accent/50',
      activeGradient: 'from-accent/10 via-accent/5 to-transparent',
    },
    {
      id: 'progressive',
      label: 'Progressive Reveal',
      description: 'Reveal words one by one',
      icon: 'Eye',
      color: 'var(--color-success)',
      activeBg: 'bg-success/15',
      activeBorder: 'border-success/50',
      activeGradient: 'from-success/10 via-success/5 to-transparent',
    }
  ];

  return (
    <div className={`bg-card rounded-lg p-4 shadow-warm ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-1">
          Practice Mode
        </h3>
        <p className="text-sm text-muted-foreground">
          Select your preferred memorization exercise
        </p>
      </div>
      <div className="grid gap-3">
        {modes?.map((mode) => {
          const isSelected = currentMode === mode?.id;
          
          return (
            <button
              key={mode?.id}
              onClick={() => onModeChange?.(mode?.id)}
              className={`text-left p-4 rounded-lg border-2 transition-all duration-300 group
                focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-card
                active:scale-[0.98]
                ${isSelected
                  ? `bg-gradient-to-r ${mode?.activeGradient} ${mode?.activeBorder} shadow-lg hover:shadow-xl hover:scale-[1.02]`
                  : 'bg-gradient-to-r from-muted/30 to-muted/10 border-border hover:from-muted/50 hover:to-muted/20 hover:border-primary/30 hover:shadow-md hover:scale-[1.02]'
                }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`flex items-center justify-center w-12 h-12 rounded-lg transition-all duration-300 ${
                  isSelected ? mode?.activeBg : 'bg-muted group-hover:bg-primary/10'
                } group-hover:scale-110 group-hover:shadow-md`}>
                  <Icon 
                    name={mode?.icon} 
                    size={24} 
                    color={isSelected ? mode?.color : 'var(--color-muted-foreground)'} 
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className={`font-medium transition-colors duration-200 ${
                      isSelected ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                    }`}>
                      {mode?.label}
                    </h4>
                    <div className="flex items-center gap-2">
                      {mode?.recommended && !isSelected && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-medium">
                          Recommended
                        </span>
                      )}
                      {isSelected && (
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded border-2 flex items-center justify-center bg-primary border-primary shadow-sm">
                            <Icon name="Check" size={12} color="white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {mode?.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {/* Mode Tips */}
      <div className="mt-4 p-3 bg-gradient-to-r from-muted/40 to-muted/20 rounded-lg border border-border/50">
        <div className="flex items-start space-x-2">
          <Icon name="Lightbulb" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Pro Tip:</p>
            {currentMode === 'fill-blank' && (
              <p className="text-xs">Start with easier verses and gradually increase difficulty. Focus on context clues to remember missing words.</p>
            )}
            {currentMode === 'word-order' && (
              <p className="text-xs">Look for natural word patterns and sentence structure. Start with familiar phrases first.</p>
            )}
            {currentMode === 'progressive' && (
              <p className="text-xs">Try to predict the next word before revealing it. This builds anticipation and memory strength.</p>
            )}
            {currentMode === 'chunking' && (
              <p className="text-xs">Listen and repeat each chunk multiple times. This progressive method strengthens long-term memory retention.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModeSelector;